import { useEffect } from "react";

import { Grid, Stack, Typography, Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CheckBoxOutlined, CreateOutlined } from "@mui/icons-material";
import nameof from "ts-nameof.macro";

import LinearStepper from "../../../common/components/LinearStepper";
import { PublicRoutesConfig } from "../../RoutesConfig";
import Success from "../../shared/SuccessModal";
import useUserContext from "../../../hooks/useUserContext";
import DatePickerExtended from "../../../common/components/mui-extended/DatePickerExtended";
import DropzoneExtended from "../../../common/components/mui-extended/DropzoneExtended";
import SectionExtended from "../../../common/components/mui-extended/SectionExtended";
import TextFieldExtended from "../../../common/components/mui-extended/TextFieldExtended";
import { ErrorMessages, Rules } from "../../../common/validationRules";
import { useAxiosLoader } from "../../../common/hooks/useAxiosLoader";
import { Step, Mode, useInputForm } from "../../../common/hooks/useInputForm";
import fileService from "../../../common/services/fileService";
import willService from "../../../services/willService";
import { WillInputDto } from "../../../interfaces/Will";
import { dateToStringShort } from "../../../common/utils/dateTime";
import { useNavigate } from "react-router-dom";

export default function Input() {
  const navigate = useNavigate();
  const loading = useAxiosLoader(false);

  const { userId, isOwner } = useUserContext();

  const defaultModel: WillInputDto = { userId };

  const form = useInputForm<WillInputDto>({
    defaultModel: defaultModel,
    addAsync: willService.addWillAsync,
    updateAsync: willService.updateWillAsync,
    validate: async (model: WillInputDto) => {
      if (model.issueDate && model.registrationNumber) {
        await willService
          .getWillIdByYearAsync(userId, model.registrationNumber, model.issueDate)
          .then(({ data: willId }) => {
            if (willId && willId !== form.id) {
              form.setError(nameof(defaultModel.registrationNumber), ErrorMessages.AlreadyExists);
            }
          });
      }
    },
  });

  useEffect(() => {
    (async () => {
      if (form.mode === Mode.EDIT) {
        await willService.getWillAsync(form.id).then(({ data: will }) => {
          const model: WillInputDto = {
            ...will,
            userId: userId,
            willId: form.id,
            fileId: will.file?.fileId,
          };

          if (isOwner(will.ownerUserIds)) {
            form.recreateForm(model);
          } else {
            navigate(PublicRoutesConfig.Home.Path);
          }
        });
      }
    })();
  }, []);

  const renderStepper = () => <LinearStepper step={form.step} mode={form.mode} />;

  const renderSuccess = () => (
    <Success routePath={PublicRoutesConfig.Will}>
      {`на завещание ${form.getValues(nameof(defaultModel.registrationNumber))} / ${dateToStringShort(
        form.getValues(nameof(defaultModel.issueDate))
      )}`}
    </Success>
  );

  const renderForm = () => (
    <form onSubmit={form.onSubmit}>
      <Stack spacing={3}>
        <SectionExtended title="Данни за завещание">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextFieldExtended
                label="Регистрационен номер"
                name={nameof(defaultModel.registrationNumber)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={{
                  ...Rules.Required,
                  ...Rules.Number,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePickerExtended
                label="Дата"
                name={nameof(defaultModel.issueDate)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={{
                  ...Rules.Required,
                  ...Rules.Date,
                }}
              />
            </Grid>
          </Grid>
        </SectionExtended>
        <SectionExtended title="Данни за завещател">
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextFieldExtended
                label="Завещател (Име, ЕГН)"
                name={nameof(defaultModel.testator)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={Rules.Required}
              />
            </Grid>
            <Grid item xs={12} md={4} />
            <Grid item xs={12} md={4}>
              <DatePickerExtended
                label="Дата на раждане"
                name={nameof(defaultModel.birthDate)}
                form={form}
                maxDate={form.watch(nameof(defaultModel.deathDate)) || new Date()}
                disabled={form.step === Step.PREVIEW}
                rules={Rules.Date}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePickerExtended
                label="Дата на смъртта"
                name={nameof(defaultModel.deathDate)}
                form={form}
                minDate={form.watch(nameof(defaultModel.birthDate))}
                disabled={form.step === Step.PREVIEW}
                rules={Rules.Date}
              />
            </Grid>
            <Grid item xs={12} md={4} />
            <Grid item xs={12} md={4}>
              <TextFieldExtended
                label="Н.Д. № и № на акта за нот. зав."
                name={nameof(defaultModel.notarialNumber)}
                form={form}
                disabled={form.step === Step.PREVIEW}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldExtended
                label="Рег. сам. зав. №"
                name={nameof(defaultModel.handwrittenNumber)}
                form={form}
                disabled={form.step === Step.PREVIEW}
              />
            </Grid>
            <Grid item xs={12} md={4} />
            <Grid item xs={12} md={8}>
              <TextFieldExtended
                label="Статус нот. зав. или акт за отмяна на нот. зав."
                name={nameof(defaultModel.status)}
                form={form}
                disabled={form.step === Step.PREVIEW}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextFieldExtended
                label="Статус саморъчно завещание"
                name={nameof(defaultModel.handwrittenStatus)}
                form={form}
                disabled={form.step === Step.PREVIEW}
              />
            </Grid>
          </Grid>
        </SectionExtended>
        <SectionExtended
          title="Сканирано копие (незадължително)"
          defaultExpanded={!!form.watch(nameof(defaultModel.fileId))}
        >
          <DropzoneExtended
            name={nameof(defaultModel.fileId)}
            form={form}
            disabled={form.step === Step.PREVIEW}
            initRequest={fileService.getFileMetadataById}
            uploadRequest={fileService.upload}
          />
        </SectionExtended>
        {form.mode === Mode.EDIT && (
          <SectionExtended title="Забележка (причини за извършване на корекцията)">
            <TextFieldExtended
              name={nameof(defaultModel.correctionReason)}
              multiline
              form={form}
              disabled={form.step === Step.PREVIEW}
              rules={Rules.Required}
            />
          </SectionExtended>
        )}
        <Stack direction="row-reverse" justifyContent="space-between">
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingPosition="start"
            size="large"
            startIcon={<CheckBoxOutlined />}
            onClick={() => form.scrollToFirstError()}
            type="submit"
          >
            {form.step === Step.INPUT ? "Преглед" : "Впиши"}
          </LoadingButton>

          {form.step === Step.PREVIEW && (
            <Button size="large" startIcon={<CreateOutlined />} onClick={() => form.onEdit()}>
              Редактирай
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );

  return (
    <Stack spacing={3} minHeight={550}>
      <Typography variant="h5">Регистър завещания</Typography>
      {renderStepper()}
      {form.step === Step.SUCCESS ? renderSuccess() : renderForm()}
    </Stack>
  );
}
